import { DOCUMENT, inject, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { RETURN_URL_KEY, type ExternalIdentityProviderType } from '@easihub/models';
import { signalStore, type, withComputed, withProps, withState } from '@ngrx/signals';
import { Dispatcher, eventGroup, Events, on, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { defer, first, firstValueFrom, map, Observable, of } from 'rxjs';
import { z } from 'zod';
import { CLIENT_CONFIG } from '../dependency-injection';
import { BETTER_AUTH_CLIENT, processBetterAuthResult, type BetterAuthClientUser, type SessionAtomData, type SessionAtomValue } from '../services/better-auth';
import { SessionMonitor } from '../services/session-monitor';
import { isServer } from '../utilities/platform-type';
import { ProblemDetails } from '@easihub/esm';

export interface RealUser {
  id: string;
  isAnonymous: false;
  email: string | null;

  name: string;
  givenName: string;
  familyName: string | null | undefined;
  image: string | null | undefined;
}

export interface AnonymousUser {
  isAnonymous: true;
}

export type AuthUser = RealUser | AnonymousUser;

interface AuthState {
  user: AuthUser;
}


export const authEvents = eventGroup({
  source: 'auth',
  events: {
    sessionChanged: type<{
      user: AuthUser;
    }>(),
    signedIn: type<{
      returnUrl: string | null;
      isNew: boolean;
    }>(),
  }
});

export type SignInCallback = (event: ReturnType<typeof authEvents['signedIn']>) => Promise<void>;
export type SignOutCallback = (event: ReturnType<typeof authEvents['signedIn']>) => Promise<void>;

export const AUTH_SIGN_IN_ACTION = new InjectionToken<SignInCallback>('AUTH_SIGN_IN_ACTION');
export const AUTH_SIGN_OUT_LOCATION = new InjectionToken<string>('AUTH_SIGN_OUT_LOCATION');
export const AUTH_SOCIAL_CALLBACK_LOCATION = new InjectionToken<string>('AUTH_SOCIAL_CALLBACK_LOCATION');

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>({
    user: {
      isAnonymous: true
    },
  }),
  withProps(() => {
    const events = inject(Events);

    return {
      ready: firstValueFrom(events.on(authEvents.sessionChanged)).then<void>(),
    };
  }),
  withComputed((store) => {
    return {
      guaranteedUser: () => {
        const user = store.user();
        if (user.isAnonymous)
          throw new Error('a real user was guaranteed');
        return user;
      },
    };
  }),
  withProps(() => {
    if (isServer()) {
      return {
        signIn: {
          email: NOOP,
          social: NOOP,
          easihub: NOOP,
          silent: NOOP,
          oauthCode: NOOP
        },
        signOut: NOOP,
        revoked: NOOP,
        copyToken: NOOP,
        // there is no session on the server — BETTER_AUTH_CLIENT throws there
        session: null
      };
    }

    const events = inject(Events);
    const client = inject(BETTER_AUTH_CLIENT);
    const dispatcher = inject(Dispatcher);
    const router = inject(Router);
    const signOutLocation = inject(AUTH_SIGN_OUT_LOCATION);
    const sessionMonitor = inject(SessionMonitor);
    const clientConfig = inject(CLIENT_CONFIG);
    const socialCallbacklocation = inject(AUTH_SOCIAL_CALLBACK_LOCATION, { optional: clientConfig.oauth.type !== 'server' });

    function handleSignIn(user: BetterAuthClientUser | null, signinState: unknown) {
      if (!user)
        throw new Error('user was expected');

      const state = userState.parse(signinState);

      dispatcher.dispatch(authEvents.signedIn({
        returnUrl: state?.[RETURN_URL_KEY] || null,
        // TODO: actually get isNew value
        isNew: false
      }));

    }

    return {
      signIn: {
        email: async (email: string, password: string, callbackURL: string) => sessionMonitor.trackSelfAction(async () => {
          if (clientConfig.oauth.type !== 'server')
            throw new Error('can only be called from accounts issuer');

          await client.signIn.email({
            email,
            password,
            callbackURL
          }).then(processBetterAuthResult);

          // the cookie is set by now — tell the other tabs before this document is replaced
          sessionMonitor.broadcast();
        }),
        social: async (provider: ExternalIdentityProviderType) => {
          if (clientConfig.oauth.type !== 'server')
            throw new Error('can only be called from accounts issuer');


          const callbackURL = new URL(clientConfig.oauth.issuer);
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          callbackURL.pathname = socialCallbacklocation!;
          callbackURL.searchParams.set(RETURN_URL_KEY, router.url);


          await client.signIn.social({
            provider,
            callbackURL: callbackURL.href
          });
        },
        easihub: async () => {
          if (clientConfig.oauth.type === 'server')
            throw new Error('cannot be called from accounts issuer');

          const signInUrl = new URL(clientConfig.oauth.issuer);
          signInUrl.pathname = '/sign-in';
          signInUrl.searchParams.set(RETURN_URL_KEY, document.location.href);

          document.location.href = signInUrl.href;

        },
      },
      signOut: async (backToRoot = true) => sessionMonitor.trackSelfAction(async () => {
        const from = router.url;
        await router.navigateByUrl(signOutLocation, { skipLocationChange: true });

        // signing out lands on a fresh anonymous session, never on nothing
        const hasSignedOut = firstValueFrom(events.on(authEvents.sessionChanged)
          .pipe(first(e => e.payload.user.isAnonymous === true)));

        // custom server-side hook will revoke
        // current real-user session
        await client.signIn.anonymous();
        await hasSignedOut;

        // this tab has settled — tell the others before any navigation replaces the document
        sessionMonitor.broadcast();

        await router.navigateByUrl(backToRoot ? '/' : from);
      }),
    };
  }),
  withReducer(
    on(authEvents.sessionChanged, ({ payload }) => {
      return {
        user: payload.user,
      };
    })
  ),
  withEventHandlers(() => {
    if (isServer()) {
      return [
        of(authEvents.sessionChanged({
          user: { isAnonymous: true }
        }))
      ];
    }

    const events = inject(Events);
    const document = inject(DOCUMENT);
    const clientConfig = inject(CLIENT_CONFIG);
    const authClient = inject(BETTER_AUTH_CLIENT);
    const sessionMonitor = inject(SessionMonitor);
    const router = inject(Router);
    const signOutLocation = inject(AUTH_SIGN_OUT_LOCATION);



    return {
      onSessionChange: defer(() => new Observable<NonNullable<SessionAtomData>>(subscriber => {

        function isSignedIn(value: SessionAtomValue | null | undefined) {
          return value?.data ? !value.data.user.isAnonymous : false;
        }
        async function externalSignOut(value: NonNullable<SessionAtomData>) {
          const from = router.url;

          const hasSignedOut = firstValueFrom(events.on(authEvents.sessionChanged)
            .pipe(first(e => e.payload.user.isAnonymous)));

          await router.navigateByUrl(signOutLocation, { skipLocationChange: true });

          subscriber.next(value);

          await hasSignedOut;

          await router.navigateByUrl(from);
        }

        async function externalSignIn(value: NonNullable<SessionAtomData>) {
          const from = router.url;

          const hasSignedIn = firstValueFrom(events.on(authEvents.sessionChanged)
            .pipe(first(e => !e.payload.user.isAnonymous)));

          subscriber.next(value);

          await hasSignedIn;

          await router.navigateByUrl(signOutLocation, { skipLocationChange: true });
          await router.navigateByUrl(from, { onSameUrlNavigation: 'reload' });
        }

        // a dead api origin costs cross-tab awareness only — it must not reach the subscriber
        sessionMonitor.start()
          .catch(err => console.error('[auth] session monitor unavailable', err));

        const unsubscribeSession = authClient.useSession
          .subscribe(async (value, oldValue) => {
            if (value.error) {
              console.error(value.error);
            }
            else if (!value.isPending && !value.isRefetching) {
              if (value.data) {
                const source = sessionMonitor.source;
                const signedIn_prev = isSignedIn(oldValue);
                const signedIn_curr = isSignedIn(value);
                if (source === 'external' && !signedIn_prev && signedIn_curr) {
                  // sign-in in another tab
                  await externalSignIn(value.data);
                  return;
                }
                else if (source !== 'self' && signedIn_prev && !signedIn_curr) {
                  // sign-out in another tab
                  await externalSignOut(value.data);
                  return;
                }
                // TODO: need to handle case of user change...?

                subscriber.next(value.data);
              }
              else {
                await navigator.locks.request('easihub:auth:mint-anon-user', { ifAvailable: true }, async lock => {
                  // another tab is already minting - its cookie will reach this
                  // one as a monitor tick, and a tick that still finds nothing
                  // brings us back here
                  if (!lock)
                    return;

                  await authClient.signIn.anonymous()
                    .then(processBetterAuthResult)
                    .catch(err => {
                      if (err instanceof ProblemDetails) {
                        if (err.type === 'better-auth:ANONYMOUS_USERS_CANNOT_SIGN_IN_AGAIN_ANONYMOUSLY') {
                          // do nothing?
                          return;
                        }
                      }
                      throw err;
                    });
                });
              }

            }
          });

        return () => {
          sessionMonitor.dispose();
          unsubscribeSession();
        };
      })).pipe(
        map(data => {
          if (data.user.isAnonymous) {
            return authEvents.sessionChanged({
              user: { isAnonymous: true }
            });
          }
          else {
            return authEvents.sessionChanged({
              user: {
                id: data.user.id,
                isAnonymous: false,
                email: data.user.email,
                givenName: data.user.givenName,
                familyName: data.user.familyName,
                name: data.user.name,
                image: data.user.image
              }
            });
          }
        })
      ),
      onPageLoad: defer(async () => {
        if (clientConfig.oauth.type !== 'server')
          return;

        const cbUrl = inject(AUTH_SOCIAL_CALLBACK_LOCATION);
        const path = document.location.pathname;

        if (cbUrl !== path)
          return;

        await sessionMonitor.announce();

        const returnUrl = new URLSearchParams(document.location.search)
          .get(RETURN_URL_KEY);

        await router.navigateByUrl(returnUrl || '/');
      })
    };
  }),
);

const NOOP = async () => void 0;

const userState = z.object({
  [RETURN_URL_KEY]: z.string().nullish(),
})
  .loose()
  .nullish();